import { HttpErrorResponse } from '@angular/common/http';

export interface IApiErrorInit {
  status: number;
  /**
   * Top-level error messages (e.g. `["the chosen slot is unavailable"]`).
   * Promoted to a first-class field rather than left in `fields` so consumers
   * and Error.message formatting can rely on a typed shape.
   */
  base?: string[];
  /**
   * Server-returned field-level errors, isolated under their own property
   * rather than spread onto the instance. Keeps Error semantics safe (no
   * accidental clobbering of `name`/`message`/`stack`/`toJSON`) and removes
   * the need for a denylist.
   */
  fields?: Record<string, unknown>;
  url?: string | null;
  method?: string;
  /**
   * Diagnostic message used as Error.message — typically Angular's
   * formatted HttpErrorResponse.message (`"Http failure response for ${url}: ${status} ${statusText}"`).
   *
   * Query strings on any embedded URL are stripped before becoming Error.message
   * (see {@link ApiError.sanitizeMessage}) so tokens/PII don't leak to telemetry.
   */
  serverMessage?: string;
  /** Sanitized cause summary. Do NOT pass the raw HttpErrorResponse — its body may contain PHI. */
  cause?: unknown;
}

/**
 * Sanitized summary of an HttpErrorResponse safe to attach as Error.cause and
 * forward to telemetry. Does NOT include `err.error` — response bodies may
 * contain PHI.
 */
export function safeCause(err: HttpErrorResponse): Record<string, unknown> {
  return {
    status: err.status,
    statusText: err.statusText,
    url: err.url,
    name: err.name
  };
}

/**
 * Error subclass thrown by HTTP error handlers. Extends Error so Sentry
 * receives a real exception with a stack and a URL-bearing message,
 * instead of an opaque plain object.
 *
 * Server-returned field-level errors live under `fields` as a typed map
 * rather than being spread onto the instance — this keeps Error semantics
 * intact and avoids the denylist hazard. `base` is promoted to a first-class
 * field because it's load-bearing for both UI consumers and message formatting.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly base?: string[];
  readonly url?: string | null;
  readonly method?: string;
  readonly fields?: Record<string, unknown>;

  constructor(init: IApiErrorInit) {
    super(ApiError.formatMessage(init), init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = 'ApiError';
    this.status = init.status;
    this.base = init.base;
    this.url = init.url ?? undefined;
    this.method = init.method;
    this.fields = init.fields;
  }

  /**
   * Strip query strings from any URL appearing in a message string. Used for
   * `serverMessage` (typically Angular's `HttpErrorResponse.message` which
   * embeds the full URL with query params).
   *
   * Query strings can carry tokens/PII and fragment Sentry grouping, so they
   * must be removed before the value becomes Error.message.
   */
  private static sanitizeMessage(message: string): string {
    return message.replace(/(\bhttps?:\/\/\S+?|\s\/\S+?)\?\S*/g, '$1');
  }

  private static formatMessage(init: IApiErrorInit): string {
    const detail = init.base?.[0];
    if (init.serverMessage) {
      // Angular's HttpErrorResponse.message is structured but doesn't include
      // the body's base errors. Append base[0] so consumers that match on
      // Error.message substrings (e.g. quick-checkout's "slot is unavailable"
      // recovery flow) still find what they need.
      const sanitized = ApiError.sanitizeMessage(init.serverMessage);
      return detail ? `${sanitized}: ${detail}` : sanitized;
    }
    const safeUrl = ApiError.stripUrlQueryString(init.url);
    const target = [init.method, safeUrl].filter(Boolean).join(' ').trim();
    const summary = target ? `HTTP ${init.status} — ${target}` : `HTTP ${init.status}`;
    return detail ? `${summary}: ${detail}` : summary;
  }

  private static stripUrlQueryString(url?: string | null): string | undefined {
    if (!url) {
      return undefined;
    }
    const queryIdx = url.indexOf('?');
    return queryIdx >= 0 ? url.slice(0, queryIdx) : url;
  }
}
